import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToaster } from "contexts/toasterContext";
import Input from "components/Input";
import Select from "react-select";
import apiService from "services/apiService";
import Button from "components/Button";
import { customSelectStyles } from "util/selectStyles";

interface OptionProps {
  value: string;
  label: string;
}

interface StructureOption {
  value: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

type UserOptionSource = { email?: string; displayName?: string };

interface CommonProps {
  refresh: number;
  setRefresh: (val: number) => void;
  onClose: () => void;
}

// NOTE: Only permanent employees carry an Annual CTC. Contractor
// (CONTRACT_EMPLOYEE / CONSULTANT) and trainee (TRAINEE / INTERN) structures are
// paid via contract / stipend values, so the Annual CTC field is disabled for
// them and required only when an employee structure is selected. The check is
// driven by the `structureType` on the selected salary structure.
const EMPLOYEE_STRUCTURE_TYPE = "PERMANENT_EMPLOYEE";
const isEmployeeStructureType = (type?: string | null) =>
  (type ?? "") === EMPLOYEE_STRUCTURE_TYPE;

const emptyToNull = (value: unknown) =>
  value === "" || value === undefined || value === null ? undefined : value;

const optionalNumberWithMessage = (
  label: string,
  min?: number,
  max?: number
) => {
  let schema = yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ||
        originalValue === undefined ||
        originalValue === null
        ? null
        : Number(originalValue)
    )
    .typeError(`${label} must be a valid number`)
    .nullable()
    .optional();

  if (min !== undefined) {
    schema = schema.min(min, `${label} must be at least ${min}`);
  }

  if (max !== undefined) {
    schema = schema.max(max, `${label} must be at most ${max}`);
  }

  return schema;
};

const validationSchema = yup.object({
  employeeEmail: yup
    .string()
    .required("Employee is required")
    .typeError("Employee is required"),

  // Mirrors the selected salary structure's structureType so the Annual CTC
  // rule below can react to it. Kept in the form (not just component state) so
  // yup's `.when` can gate CTC required/optional without a custom resolver.
  structureType: yup.string().nullable().optional(),

  // Annual CTC is REQUIRED for employee structures and optional otherwise
  // (contractor / trainee, where the field is disabled). When provided it must
  // still meet the minimum.
  // NOTE: inspect the RAW input (originalValue), not the coerced value — yup
  // casts an empty string to NaN, so an emptyToNull check on the coerced value
  // would leak NaN through and wrongly trigger "must be a number" on submit.
  annualCtc: yup
    .number()
    .transform((_value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : Number(originalValue)
    )
    .nullable()
    .when("structureType", {
      is: (type: string) => isEmployeeStructureType(type),
      then: (schema) =>
        schema
          .required("Annual CTC is required")
          .min(100000, "Minimum CTC is ₹1,00,000")
          .typeError("Annual CTC must be a number"),
      otherwise: (schema) =>
        schema
          .optional()
          .min(100000, "Minimum CTC is ₹1,00,000")
          .typeError("Annual CTC must be a number"),
    }),

  year: yup
    .number()
    .transform(emptyToNull)
    .required("Year is required")
    .typeError("Year must be a number"),

  salaryAdvance: optionalNumberWithMessage("Salary Advance").test(
    "is-less-than-ctc",
    "Salary Advance must be less than Annual CTC",
    function (value) {
      const { annualCtc } = this.parent;
      if (value === null || value === undefined || annualCtc === undefined)
        return true;
      return value < annualCtc;
    }
  ),

  overrideStructureId: optionalNumberWithMessage("Override Structure ID"),

  tdsOverride: optionalNumberWithMessage("TDS Override"),

  gratuityPercentage: optionalNumberWithMessage("Gratuity"),

  remarks: yup.string().optional().typeError("Remarks must be a string"),
});

const AddSalary: React.FC<CommonProps> = ({ refresh, setRefresh, onClose }) => {
  const [structureOptions, setStructureOptions] = useState<StructureOption[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedStructure, setSelectedStructure] = useState<any>(null);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [tdsApplicable, setTdsApplicable] = useState(true);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      year: new Date().getFullYear(),
    },
  });

  const [emailData, setEmailData] = useState<OptionProps[]>([]);
  // NOTE: the picked option is kept in state instead of being looked up in
  // emailData — searching replaces that list, which used to make the current
  // selection disappear from the control.
  const [selectedEmployee, setSelectedEmployee] = useState<OptionProps | null>(null);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const USER_PAGE_SIZE = 10;
  // Identifies the newest request so a slow earlier response can't overwrite it
  const userRequestRef = useRef(0);
  // Search term the loaded option list belongs to (null = nothing loaded yet)
  const loadedUserSearchRef = useRef<string | null>(null);

  const getUsersFromPayload = (payload: unknown): UserOptionSource[] => {
    if (Array.isArray(payload)) {
      return payload as UserOptionSource[];
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      "content" in payload &&
      Array.isArray((payload as { content?: unknown }).content)
    ) {
      return (payload as { content: UserOptionSource[] }).content;
    }

    return [];
  };

  const { addToast } = useToaster();

  useEffect(() => {
    setLoadingStructures(true);
    apiService
      .get(`api/salary/getAllSalaryStructures`)
      .then((response) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const list: any[] = Array.isArray(response.data)
          ? response.data
          : [response.data];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const options = list.map((s: any) => ({
          value: s.structureName ?? String(s.id ?? ""),
          label: s.structureName ?? `Structure ${s.id ?? ""}`,
          data: s,
        }));
        setStructureOptions(options);
      })
      .catch(() => {
        apiService
          .get(`api/salary/commonSalaryStructure`)
          .then((res) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const list: any[] = Array.isArray(res.data) ? res.data : [res.data];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const options = list.map((s: any) => ({
              value: s.structureName ?? "default",
              label: s.structureName ?? "Default Structure",
              data: s,
            }));
            setStructureOptions(options);
          })
          .catch((err) => {
            console.error("Error fetching salary structures:", err);
          });
      })
      .finally(() => setLoadingStructures(false));
  }, []);

  const handleStructureChange = (option: StructureOption | null) => {
    setSelectedStructure(option ? option.data : null);
  };

  // Annual CTC only applies to employee structures. Disable it for contractor /
  // trainee structures and require it when an employee structure is selected.
  const structureType: string = selectedStructure?.structureType ?? "";
  const isCtcRequired = isEmployeeStructureType(structureType);
  const isCtcDisabled = structureType !== "" && !isCtcRequired;

  // Keep the form's structureType in sync so the yup `.when` gate re-validates,
  // and clear a stale CTC when switching to a structure that disables the field.
  useEffect(() => {
    methods.setValue("structureType", structureType, { shouldValidate: true });
    if (isCtcDisabled) {
      methods.setValue("annualCtc", "" as unknown as number, {
        shouldValidate: true,
      });
      methods.clearErrors("annualCtc");
    }
  }, [structureType, isCtcDisabled]);

  const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    /* NOTE: this used to bail out with `if (loadingUsers) return;`. Clearing the
       search box fired a refetch while the previous search request was still in
       flight, so the reload of the full list was dropped and never retried —
       the dropdown kept showing the old (often empty) filtered list as
       "No options". Requests are now always issued and tagged, and only the
       newest response is applied. */
    const requestId = ++userRequestRef.current;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const response = await apiService.get(`api/users?${query.toString()}`);

      // A newer search has started — discard this outdated response
      if (requestId !== userRequestRef.current) return;

      const payload = response.data;
      const users = getUsersFromPayload(payload);

      const options = users.map((employee: { email?: string; displayName?: string }) => ({
        value: employee.email || "",
        label: employee.displayName || "",
      }));

      setEmailData((prev) => {
        const merged = append ? [...prev, ...options] : options;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return Array.from(dedup.values());
      });

      // Remember which search the loaded list belongs to
      loadedUserSearchRef.current = searchValue;
      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      // Only the newest request controls the loading indicator
      if (requestId === userRequestRef.current) {
        setLoadingUsers(false);
      }
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  /**
   * Reload the full list when the menu is opened with an empty search box.
   * NOTE: needed because setUserSearch("") is a no-op when the term is already
   * "" (e.g. it was reset on menu-close), so the debounced effect above does not
   * re-run and the stale filtered list would be shown as "No options".
   */
  const handleUserMenuOpen = () => {
    if (!userSearch && loadedUserSearchRef.current !== "") {
      void fetchUsers(0, "", false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (data: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toNumber = (val: any) =>
      val === undefined || val === null ? 0 : Number(val);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toStringOrZero = (val: any) =>
      val === undefined || val === null || val === "" ? "0" : String(val);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      employeeEmail: data.employeeEmail,
      annualCtc: toNumber(data.annualCtc),
      year: toNumber(data.year),
      salaryAdvance: toNumber(data.salaryAdvance),
      overrideStructureId: toNumber(data.overrideStructureId),
      remarks: toStringOrZero(data.remarks),
      gratuityPercentage: toNumber(data.gratuityPercentage),
      ...(!tdsApplicable && { tdsOverride: 0 }),
    };

    if (selectedStructure?.structureName) {
      payload.structureName = selectedStructure.structureName;
    }

    apiService
      .post("/api/salary", payload)
      .then(() => {
        setRefresh(refresh + 1);
        addToast("Salary details added successfully", "success");
      })
      .catch((error) => {
        addToast(error.response.data, "error");
      })
      .finally(() => onClose());
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)} className="px-3 pt-0 pb-2 flex flex-col gap-2">

        <div className="flex flex-col gap-2 w-full">

          {/* Top Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2 p-2">

            {/* Employee Select */}
            <div>
              <label className="text-sm font-medium">
                Select Employee Name <span className="text-red-400">*</span>
              </label>

              <Select
                options={emailData}
                styles={customSelectStyles}
                isLoading={loadingUsers}
                placeholder="Select Employee"
                value={selectedEmployee}
                onChange={(selectedOption) => {
                  setSelectedEmployee(selectedOption ?? null);
                  methods.setValue("employeeEmail", selectedOption?.value ?? "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                onMenuOpen={handleUserMenuOpen}
                onMenuScrollToBottom={() => {
                  if (hasMoreUsers && !loadingUsers) {
                    void fetchUsers(userPage + 1, userSearch, true);
                  }
                }}
                onInputChange={(value, actionMeta) => {
                  // "input-change" also covers clearing the box, which reloads
                  // the unfiltered list through the debounced effect
                  if (actionMeta.action === "input-change") {
                    setUserSearch(value);
                  }
                  if (actionMeta.action === "menu-close") {
                    setUserSearch("");
                  }
                  return value;
                }}
                noOptionsMessage={() =>
                  loadingUsers ? "Loading employees…" : "No employees found"
                }
                className="mt-1"
              />

              {methods.formState.errors.employeeEmail && (
                <p className="text-red-500 text-sm">
                  {methods.formState.errors.employeeEmail.message as string}
                </p>
              )}
            </div>

            {/* Inputs */}
            {/* Annual CTC: required for employee structures, disabled for
                contractor / trainee structures (driven by structureType). */}
            <Input
              name="annualCtc"
              label="Annual CTC"
              type="number"
              placeholder={isCtcDisabled ? "Not applicable" : "Annual CTC in (₹)"}
              required={isCtcRequired}
              disabled={isCtcDisabled}
            />
            <Input name="year" label="Year" type="number" required />
            <Input name="salaryAdvance" label="Salary Advance" type="number" placeholder="Advance Salary (₹)" />

            {/* TDS Toggle */}
            <div className="flex flex-col justify-center gap-1">
              <label className="text-sm font-medium">TDS Applicable</label>
              <button
                type="button"
                onClick={() => setTdsApplicable((prev) => !prev)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${tdsApplicable ? "bg-blue-600" : "bg-gray-300"
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${tdsApplicable ? "translate-x-6" : "translate-x-1"
                    }`}
                />
              </button>
              <span className="text-xs text-gray-500">
                {tdsApplicable ? "Auto-calculated" : "Disabled (₹0)"}
              </span>
            </div>
          </div>

          {/* Salary Structure Dropdown */}
          <div className="px-2">
            <label className="block text-sm font-medium mb-1">
              Salary Structure
            </label>
            <Select
              options={structureOptions}
              styles={customSelectStyles}
              isLoading={loadingStructures}
              isClearable
              placeholder="Select a salary structure (optional)"
              value={
                selectedStructure
                  ? structureOptions.find(
                    (o) => o.value === (selectedStructure.structureName ?? "")
                  ) ?? null
                  : null
              }
              onChange={(option) =>
                handleStructureChange(option as StructureOption | null)
              }
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-1 flex justify-end px-2 gap-2">
          <Button
            type="button"
            onClick={onClose}
            className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95"
          >
            Cancel
          </Button>
          <Button
            disabled={!methods.formState.isDirty}
            className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
            type="submit"
          >
            Submit
          </Button>
        </div>

      </form>
    </FormProvider>
  );
};

export default AddSalary;
